import React, { useMemo } from 'react';
import { Box, ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { useTranslation } from '@shared/core';
import { HotelInventory } from '@apps/admin/routes/Planning/hooks/HotelListSearcher/useZentrumHubHotelListSearcher';
import { ResponsiveDialog } from '@shared/components/ResponsiveDialog';
import { closeButtonOverridesV2, dialogOverridesV2, drawerOverridesV2 } from '@shared/components/ResponsiveDialog/ResponsiveDialog.styles';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { MatchedHotelPrice } from './MatchedHotelPrice';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { StarRating } from '@shared/components';
import { HotelPhotoCarousel } from '@shared/components/HotelPhotoCarousel';
import { usePricingByJoyPlaceIdQuery } from '@shared/hooks/usePricingByPropertyIds';
import { useEventInfo } from '@shared/utils/eventInfo';
import { DistanceFromVenue } from '@shared/components/DistanceFromVenue';

interface Props
  extends Readonly<{
    isOpen: boolean;
    onClose: (keep?: boolean) => void;
    onAddHotelFromMatchedHotel?: (hotelInventory: HotelInventory) => void;
    hotelInventory: HotelInventory | null;
    joyPlaceId?: string | null;
  }> {}

export const MatchedHotelDialog: React.FC<Props> = ({ isOpen, onClose, hotelInventory, joyPlaceId: joyPlaceIdArg, onAddHotelFromMatchedHotel }) => {
  const { t } = useTranslation('websiteEditor');
  const matchedHotelTrans = t('customPage', 'matchedHotelDialog');
  const [isMobile] = useResponsive({ values: { mobile: true, tablet: false } });
  const { eventInfo, loading: eventInfoLoading } = useEventInfo();

  const joyPlaceId = hotelInventory?.joyPlaceId || joyPlaceIdArg;

  const images = useMemo(() => {
    if (hotelInventory?.photos) {
      return hotelInventory.photos.map(photo => ({ src: photo, alt: hotelInventory.displayName }));
    } else if (hotelInventory?.photo) {
      return [{ src: hotelInventory.photo, alt: hotelInventory.displayName }];
    }
    return [];
  }, [hotelInventory?.displayName, hotelInventory?.photo, hotelInventory?.photos]);

  const handleAdd = useEventCallback(() => {
    if (hotelInventory) {
      onAddHotelFromMatchedHotel?.(hotelInventory);
    }
    onClose();
  });

  return (
    <ResponsiveDialog
      dialogOverrides={{ ...dialogOverridesV2, Content: { props: { maxWidth: pxToRem(540) } }, Footer: { props: { paddingX: 80 } } }}
      drawerOverrides={drawerOverridesV2}
      closeButtonOverrides={closeButtonOverridesV2}
      isOpen={isOpen}
      hasCloseButton={false}
      onClose={() => onClose(true)}
      hideDivider={true}
      removeBodyPadding={true}
      hideFooterDivider={!isMobile}
      renderFooter={() => {
        return (
          <Flex
            flexDirection="column"
            width="100%"
            gap={5}
            justifyContent="center"
          >
            <ButtonV2
              typographyVariant="button2"
              marginTop={5}
              width="100%"
              shape="rounded"
              color="white"
              backgroundColor="newUITest1"
              onClick={handleAdd}
            >
              {matchedHotelTrans.saveWithJoy()}
            </ButtonV2>
            <ButtonV2
              typographyVariant="button2"
              width="100%"
              color="mono14"
              variant="ghost"
              onClick={() => onClose(true)}
            >
              {matchedHotelTrans.haveBookingLink()}
            </ButtonV2>
          </Flex>
        );
      }}
    >
      <Flex
        flexDirection={'column'}
        alignItems={'center'}
        paddingX={{ _: 6, sm: 40, md: 80 }}
        paddingTop={{ _: 6, sm: 7 }}
        gap={5}
      >
        <Box
          borderRadius={12}
          border={{ _: 'none', sm: '1px solid' }}
          borderColor={{ _: 'none', sm: 'mono3' }}
          padding={{ _: 0, md: 6 }}
          width="100%"
          maxWidth={{ _: pxToRem(330), sm: pxToRem(312) }}
        >
          {!!images.length && hotelInventory && (
            <Box
              width="100%"
              height="100%"
            >
              <Box
                aspectRatio="3 / 2"
                position={'relative'}
                backgroundColor={'mono2'}
                overflow={'hidden'}
                borderRadius={pxToRem(12)}
              >
                <HotelPhotoCarousel
                  hotel={hotelInventory}
                  images={images}
                  context="Accommodations.MatchedHotelDialog"
                  aspectRatio="3 / 2"
                  navigation={{ variant: 'minimal', visible: 'default' }}
                />
              </Box>
            </Box>
          )}
          <TextV2
            typographyVariant="hed2"
            marginTop={5}
          >
            {hotelInventory?.displayName}
          </TextV2>
          <Flex
            gap={5}
            marginTop={3}
            alignItems="center"
            flexWrap="wrap"
          >
            <StarRating
              id="star-rating-matched-hotel-dialog"
              showPartialRating
              rating={hotelInventory?.starRating || 0}
            />
            <DistanceFromVenue milesToVenue={hotelInventory?.distance} />
          </Flex>
          {/* To be done after finalizing the price api behavior for not-added hotels  */}
          {!eventInfoLoading && joyPlaceId && (
            <MatchedHotelPricingQuery
              hotelJoyPlaceIds={[joyPlaceId]}
              eventDate={eventInfo?.finalizedDate?.milliseconds}
            />
          )}
          {joyPlaceId && (
            <Flex
              flexDirection="column"
              gap={3}
            >
              <MatchedHotelPrice joyPlaceId={joyPlaceId} />
            </Flex>
          )}
        </Box>
        <TextV2
          typographyVariant="body2"
          paddingY={{ _: 5, sm: 0 }}
          maxWidth={pxToRem(312)}
          textAlign="center"
        >
          {matchedHotelTrans.description()}
        </TextV2>
      </Flex>
    </ResponsiveDialog>
  );
};

const MatchedHotelPricingQuery = (props: { hotelJoyPlaceIds: string[]; eventDate: Maybe<number> }) => {
  usePricingByJoyPlaceIdQuery(props.hotelJoyPlaceIds, props.eventDate);
  return null;
};
